import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import AuthGuard from './components/AuthGuard';
import reportWebVitals from './reportWebVitals';

// Force light mode - prevent dark mode from being applied
if (document.documentElement) {
  document.documentElement.style.colorScheme = 'light only';
  // Remove any dark class that might be set by the browser
  document.documentElement.classList.remove('dark');
  // Ensure light mode is set
  document.documentElement.setAttribute('data-theme', 'light');
}

// Also set it on the body
if (document.body) {
  document.body.style.colorScheme = 'light only';
}

// Suppress harmless ResizeObserver loop warning. Het is een browser-niveau
// warning die optreedt wanneer een ResizeObserver-callback nog een resize
// triggert binnen dezelfde frame — de browser stopt de loop zelf en gaat
// door. CRA's dev error overlay laat 'm zien als ware het een fatale fout.
// Zie: https://github.com/WICG/resize-observer/issues/38
//
// Belangrijk: webpack-dev-server registreert zijn overlay-handler in de
// CAPTURE-fase, dus we moeten OOK in capture-fase luisteren én vóór de
// overlay-listener (= zo vroeg mogelijk in de app-bootstrap).
const RESIZE_OBSERVER_LOOP_ERR =
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;

const swallowResizeObserverError = (e: ErrorEvent) => {
  if (e.message && RESIZE_OBSERVER_LOOP_ERR.test(e.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
};
// Capture-fase + bubble-fase voor zekerheid
window.addEventListener('error', swallowResizeObserverError, true);
window.addEventListener('error', swallowResizeObserverError, false);

window.addEventListener('unhandledrejection', (e) => {
  const msg = (e.reason && (e.reason.message || String(e.reason))) || '';
  if (RESIZE_OBSERVER_LOOP_ERR.test(msg)) {
    e.preventDefault();
  }
}, true);

// Sommige overlays luisteren ook naar console.error met deze message.
const origConsoleError = console.error;
console.error = (...args: any[]) => {
  const first = args[0];
  if (typeof first === 'string' && RESIZE_OBSERVER_LOOP_ERR.test(first)) {
    return;
  }
  if (first instanceof Error && RESIZE_OBSERVER_LOOP_ERR.test(first.message)) {
    return;
  }
  origConsoleError.apply(console, args);
};

// webpack-dev-server v4+ injecteert de error overlay als een dedicated iframe
// (id 'webpack-dev-server-client-overlay'). Die iframe leeft buiten React's
// boom én buiten onze console/window error handlers omdat hij met zijn eigen
// document werkt. Strategie: MutationObserver die de iframe weghaalt zodra hij
// verschijnt EN de RESIZE_OBSERVER message bevat. Andere echte errors blijven
// gewoon zichtbaar.
const removeOverlayIfResizeWarning = () => {
  const overlay = document.getElementById(
    'webpack-dev-server-client-overlay'
  ) as HTMLIFrameElement | null;
  if (!overlay) return;
  try {
    const doc = overlay.contentDocument;
    const text = doc?.body?.textContent || '';
    if (RESIZE_OBSERVER_LOOP_ERR.test(text)) {
      overlay.remove();
    }
  } catch {
    // iframe nog niet geladen — probeer nog een keer na load
    overlay.addEventListener('load', () => {
      try {
        const doc = overlay.contentDocument;
        const text = doc?.body?.textContent || '';
        if (RESIZE_OBSERVER_LOOP_ERR.test(text)) {
          overlay.remove();
        }
      } catch {
        /* ignore */
      }
    });
  }
};

const overlayObserver = new MutationObserver(() => {
  removeOverlayIfResizeWarning();
});
// Wacht tot DOM klaar is voordat we observen
if (document.body) {
  overlayObserver.observe(document.body, { childList: true, subtree: true });
} else {
  document.addEventListener('DOMContentLoaded', () => {
    overlayObserver.observe(document.body, { childList: true, subtree: true });
  });
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <AuthGuard>
      <App />
    </AuthGuard>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
